import CustomTable from "@/shared/components/ui/Button/CustomTable";
import PaginationInfo from "@/shared/components/ui/Pagination/PaginationInfo";
import SearchBar from "@/shared/components/ui/SearchBar/SearchBar";
import { ArrowLeft } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useFetchSuperAdminOrgUsers } from "./hooks/useSuperAdminApiHooks";
import {
  formatDate,
  getTextInCapitalize,
} from "@/shared/utils/helperFunctions";
import Loader from "@/shared/components/ui/Loaders/Loader";

const USER_COLUMNS = [
  { uid: "name", name: "Name", sortable: true, align: "left", width: "160px" },
  {
    uid: "email",
    name: "Email",
    sortable: true,
    align: "left",
    width: "210px",
  },
  { uid: "role", name: "Role", sortable: true, align: "left", width: "110px" },
  { uid: "plan", name: "Plan", sortable: true, align: "left", width: "90px" },
  {
    uid: "trial_status",
    name: "Trial status",
    sortable: true,
    align: "left",
    width: "130px",
  },
  {
    uid: "created_at",
    name: "Joined",
    sortable: true,
    align: "left",
    width: "120px",
  },
];

const OrganizationUsers = () => {
  const { orgId } = useParams();
  const [filterValue, setFilterValue] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const { data, isLoading, isError, error } = useFetchSuperAdminOrgUsers(
    orgId,
    { limit: 500, offset: 0 },
  );

  const users = useMemo(() => data?.users ?? [], [data?.users]);

  const orgTitle = useMemo(() => {
    const name = users[0]?.organization_name;
    return name ? getTextInCapitalize(name) : "Organization";
  }, [users]);

  const filteredData = useMemo(() => {
    if (!filterValue) return users;
    const q = filterValue.toLowerCase();
    return users.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.role || "").toLowerCase().includes(q),
    );
  }, [users, filterValue]);

  const renderCell = useCallback((row, columnKey) => {
    switch (columnKey) {
      case "name":
        return (
          <span className="font-medium text-gray-800">
            {getTextInCapitalize(row.name || "—")}
          </span>
        );
      case "email":
        return <span className="text-gray-600">{row.email}</span>;
      case "role":
        return (
          <span className="text-gray-600">
            {row.role === "org_admin"
              ? "Org Admin"
              : row.role === "user"
                ? "User"
                : row.role || "—"}
          </span>
        );
      case "plan":
        return (
          <span className="text-gray-600">
            {row.plan ? getTextInCapitalize(String(row.plan)) : "—"}
          </span>
        );
      case "trial_status":
        if (row.plan === "reject")
          return <span className="text-error-600 text-sm">Rejected</span>;
        if (row.trial_approved_at)
          return <span className="text-success-600 text-sm">Approved</span>;
        return <span className="text-warning-600 text-sm">Pending</span>;
      case "created_at":
        return (
          <span className="text-gray-600">
            {row.created_at ? formatDate(row.created_at) : "—"}
          </span>
        );
      default:
        return <span className="text-gray-600">{row[columnKey]}</span>;
    }
  }, []);

  const onSearchChange = useCallback((value) => {
    setFilterValue(value);
    setPage(1);
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Link
          to="/sa/organizations"
          className="flex items-center gap-2 text-secondary hover:underline w-fit"
        >
          <ArrowLeft size={16} />
          Back to Organizations
        </Link>
        <div className="flex items-center justify-center min-h-[40vh]">
          <Loader text="Loading members..." />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        <Link
          to="/sa/organizations"
          className="flex items-center gap-2 text-secondary hover:underline w-fit"
        >
          <ArrowLeft size={16} />
          Back to Organizations
        </Link>
        <p className="text-error-600">
          {error?.message || "Failed to load organization members."}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <Link
          to="/sa/organizations"
          className="flex items-center gap-2 text-secondary hover:underline w-fit"
        >
          <ArrowLeft size={16} />
          Back
        </Link>
      </div>

      <h1 className="text-[1.375rem] text-gray-800 font-bold">
        {orgTitle} — Members
      </h1>

      <div className="flex flex-col gap-4 w-full">
        <div className="flex justify-between items-center gap-4">
          <div className="flex gap-2 items-center w-full">
            <SearchBar
              value={filterValue}
              setFilterValue={setFilterValue}
              onChange={onSearchChange}
              placeholder="Search members..."
              className="max-w-[280px]"
            />
            <PaginationInfo
              totalCount={filteredData.length}
              currentPage={page}
              rowsPerPage={rowsPerPage}
              itemName="member"
            />
          </div>
        </div>

        {filteredData.length > 0 ? (
          <CustomTable
            renderCell={renderCell}
            columns={USER_COLUMNS}
            data={filteredData}
            totalCount={filteredData.length}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            page={page}
            onPageChange={setPage}
            height={"calc(100dvh - 180px)"}
          />
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <p className="text-gray-500">No members found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationUsers;
